import { useState, useEffect } from 'react'
import parse, { type HTMLReactParserOptions, Element, domToReact, type DOMNode } from 'html-react-parser'
import { Link } from '@tanstack/react-router'
import { renderMarkdown, type MarkdownResult } from '@/lib/markdown'
import { Skeleton } from '@/components/ui/skeleton'
import { codeToHtml } from 'shiki'
import { useTheme } from '@/components/theme-provider'
import { cn } from '@/lib/utils'

// ── Markdown-embedded UI components ──────────────────────────────────────────

type CalloutType = 'note' | 'tip' | 'warning'

const calloutStyles: Record<CalloutType, string> = {
  note: 'border-border bg-muted/40 text-muted-foreground',
  tip: 'border-primary/30 bg-primary/5 text-foreground',
  warning: 'border-amber-500/30 bg-amber-500/5 text-foreground',
}

const calloutLabels: Record<CalloutType, string> = {
  note: 'Note',
  tip: 'Tip',
  warning: 'Warning',
}

export function Callout({
  type = 'note',
  children,
}: {
  type?: CalloutType
  children: React.ReactNode
}) {
  return (
    <div className={cn('rounded-lg border px-4 py-3.5 text-sm leading-relaxed my-5', calloutStyles[type])}>
      <span className="font-semibold mr-1.5">{calloutLabels[type]}:</span>
      {children}
    </div>
  )
}

export function Steps({ children }: { children: React.ReactNode }) {
  return <div className="space-y-0">{children}</div>
}

export function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-5">
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center shrink-0">
          {n}
        </div>
        <div className="w-px flex-1 bg-border/50 mt-3" />
      </div>
      <div className="pb-10 flex-1 min-w-0">
        <h2
          id={`step-${n}`}
          className="text-xl font-bold text-foreground mb-4 scroll-mt-24"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          {title}
        </h2>
        {children}
      </div>
    </div>
  )
}

export function CardGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid sm:grid-cols-2 gap-4 mt-2">{children}</div>
}

export function DocCard({
  href,
  title,
  description,
}: {
  href: string
  title: string
  description: string
}) {
  return (
    <a
      href={href}
      className="group rounded-xl border border-border/60 bg-card p-5 hover:border-primary/40 hover:bg-primary/5 transition-colors no-underline block"
    >
      <p className="font-semibold text-foreground mb-1">{title}</p>
      <p className="text-sm text-muted-foreground">{description}</p>
    </a>
  )
}

// ── Internal helpers ──────────────────────────────────────────────────────────

function getTextContent(node: any): string {
  if (!node) return ''
  if (node.type === 'text') return node.data || ''
  if (node.children) return node.children.map(getTextContent).join('')
  return ''
}

function HighlightedCode({ code, language, isDark }: { code: string; language: string; isDark: boolean }) {
  const [highlighted, setHighlighted] = useState<string | null>(null)

  useEffect(() => {
    codeToHtml(code, {
      lang: language || 'text',
      theme: isDark ? 'github-dark' : 'github-light',
    }).then((html) => {
      const htmlWithoutBg = html.replace(/style="[^"]*background-color:[^;"]*;?[^"]*"/g, (match) =>
        match.replace(/background-color:[^;]*;?/g, '')
      )
      setHighlighted(htmlWithoutBg)
    })
  }, [code, language, isDark])

  if (!highlighted) {
    return (
      <pre className="bg-muted p-6 overflow-x-auto">
        <code className="text-sm font-mono text-foreground">{code}</code>
      </pre>
    )
  }

  return (
    <div
      className="bg-muted overflow-x-auto p-6 [&_pre]:m-0 [&_pre]:p-0"
      dangerouslySetInnerHTML={{ __html: highlighted }}
    />
  )
}

function CodeBlock({ code, language, isDark }: { code: string; language: string; isDark: boolean }) {
  return (
    <div className="my-4 rounded-lg overflow-hidden border border-border">
      <div className="bg-muted px-4 py-2 text-xs text-muted-foreground font-mono border-b border-border">
        {language || 'text'}
      </div>
      <HighlightedCode code={code} language={language} isDark={isDark} />
    </div>
  )
}

function Heading({ level, children, className }: { level: number; children: React.ReactNode; className: string }) {
  switch (level) {
    case 1: return <h1 className={className}>{children}</h1>
    case 2: return <h2 className={className}>{children}</h2>
    case 3: return <h3 className={className}>{children}</h3>
    case 4: return <h4 className={className}>{children}</h4>
    case 5: return <h5 className={className}>{children}</h5>
    case 6: return <h6 className={className}>{children}</h6>
    default: return <h3 className={className}>{children}</h3>
  }
}

// ── Markdown component ────────────────────────────────────────────────────────

type MarkdownProps = {
  content: string
  className?: string
}

export function Markdown({ content, className }: MarkdownProps) {
  const [result, setResult] = useState<MarkdownResult | null>(null)
  const [loading, setLoading] = useState(true)
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  useEffect(() => {
    setLoading(true)
    renderMarkdown(content)
      .then(setResult)
      .finally(() => setLoading(false))
  }, [content])

  if (loading) {
    return (
      <div className={className}>
        <div className="space-y-3">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/6" />
        </div>
      </div>
    )
  }

  if (!result) {
    return <div className={className}>Failed to render content</div>
  }

  const getHeadingClasses = (level: number) => {
    const baseClasses = 'text-foreground font-bold scroll-mt-20'
    const sizeClasses: Record<number, string> = {
      1: 'text-4xl mb-8',
      2: 'text-2xl mb-6 mt-8',
      3: 'text-xl mb-4 mt-6',
      4: 'text-lg mb-3 mt-4',
      5: 'text-base mb-2 mt-3',
      6: 'text-sm mb-2 mt-3',
    }
    return `${baseClasses} ${sizeClasses[level] ?? sizeClasses[3]}`
  }

  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (!(domNode instanceof Element)) return

      // ── Directive components ────────────────────────────────────────────────

      if (domNode.name === 'div' && domNode.attribs?.class === 'md-callout') {
        const type = (domNode.attribs['data-type'] || 'note') as CalloutType
        return <Callout type={type}>{domToReact(domNode.children as DOMNode[], options)}</Callout>
      }

      if (domNode.name === 'div' && domNode.attribs?.class === 'md-steps') {
        return <Steps>{domToReact(domNode.children as DOMNode[], options)}</Steps>
      }

      if (domNode.name === 'div' && domNode.attribs?.class === 'md-step') {
        const n = parseInt(domNode.attribs['data-n'] || '1', 10)
        const title = domNode.attribs['data-title'] || ''
        return <Step n={n} title={title}>{domToReact(domNode.children as DOMNode[], options)}</Step>
      }

      if (domNode.name === 'div' && domNode.attribs?.class === 'md-card-grid') {
        return <CardGrid>{domToReact(domNode.children as DOMNode[], options)}</CardGrid>
      }

      if (domNode.name === 'a' && domNode.attribs?.class === 'md-card') {
        return (
          <DocCard
            href={domNode.attribs.href || '#'}
            title={domNode.attribs['data-title'] || ''}
            description={domNode.attribs['data-description'] || ''}
          />
        )
      }

      // ── Code blocks ─────────────────────────────────────────────────────────

      if (domNode.name === 'pre') {
        const codeEl = domNode.children.find(
          (c): c is Element => c instanceof Element && c.name === 'code'
        )
        if (codeEl) {
          const language = (codeEl.attribs?.class || '').replace('language-', '')
          return <CodeBlock code={getTextContent(codeEl)} language={language} isDark={isDark} />
        }
      }

      // ── Headings ────────────────────────────────────────────────────────────

      if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(domNode.name)) {
        const level = parseInt(domNode.name.charAt(1), 10)
        const anchorChild = domNode.children.find(
          (c): c is Element => c instanceof Element && c.name === 'a'
        )
        if (anchorChild?.attribs?.class?.includes('anchor')) {
          return (
            <Heading level={level} className={getHeadingClasses(level)}>
              <a
                href={anchorChild.attribs.href}
                className="group no-underline text-foreground hover:text-foreground"
              >
                {domToReact(anchorChild.children as DOMNode[], options)}
              </a>
            </Heading>
          )
        }
        return (
          <Heading level={level} className={getHeadingClasses(level)}>
            {domToReact(domNode.children as DOMNode[], options)}
          </Heading>
        )
      }

      // ── Links ────────────────────────────────────────────────────────────────

      if (domNode.name === 'a') {
        if (domNode.attribs?.class?.includes('anchor')) return undefined
        const href = domNode.attribs?.href
        if (href?.startsWith('/') && !href.startsWith('//')) {
          return (
            <Link to={href} className="text-primary hover:text-primary/80 underline">
              {domToReact(domNode.children as DOMNode[], options)}
            </Link>
          )
        }
        return (
          <a
            href={href}
            className="text-primary hover:text-primary/80 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {domToReact(domNode.children as DOMNode[], options)}
          </a>
        )
      }

      // ── Block elements ───────────────────────────────────────────────────────

      if (domNode.name === 'p') {
        return (
          <p className="mb-4 text-muted-foreground leading-relaxed">
            {domToReact(domNode.children as DOMNode[], options)}
          </p>
        )
      }

      if (domNode.name === 'ul') {
        return (
          <ul className="mb-4 space-y-1.5 text-muted-foreground list-disc pl-6">
            {domToReact(domNode.children as DOMNode[], options)}
          </ul>
        )
      }

      if (domNode.name === 'ol') {
        return (
          <ol className="mb-4 space-y-1.5 text-muted-foreground list-decimal pl-6">
            {domToReact(domNode.children as DOMNode[], options)}
          </ol>
        )
      }

      if (domNode.name === 'li') {
        return (
          <li className="leading-relaxed">
            {domToReact(domNode.children as DOMNode[], options)}
          </li>
        )
      }

      if (domNode.name === 'blockquote') {
        return (
          <blockquote className="border-l-4 border-border pl-4 italic text-muted-foreground mb-4">
            {domToReact(domNode.children as DOMNode[], options)}
          </blockquote>
        )
      }

      if (domNode.name === 'hr') {
        return <hr className="border-border/50 my-8" />
      }

      // ── Tables ───────────────────────────────────────────────────────────────

      if (domNode.name === 'table') {
        return (
          <div className="overflow-x-auto my-6 rounded-xl border border-border/60">
            <table className="w-full text-sm">
              {domToReact(domNode.children as DOMNode[], options)}
            </table>
          </div>
        )
      }

      if (domNode.name === 'thead') {
        return (
          <thead className="bg-muted/40 border-b border-border/50">
            {domToReact(domNode.children as DOMNode[], options)}
          </thead>
        )
      }

      if (domNode.name === 'th') {
        return (
          <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground">
            {domToReact(domNode.children as DOMNode[], options)}
          </th>
        )
      }

      if (domNode.name === 'td') {
        return (
          <td className="px-4 py-2.5 border-t border-border/30 text-muted-foreground">
            {domToReact(domNode.children as DOMNode[], options)}
          </td>
        )
      }

      // ── Inline elements ──────────────────────────────────────────────────────

      const parentEl = domNode.parent instanceof Element ? domNode.parent : null
      if (domNode.name === 'code' && (!parentEl || parentEl.name !== 'pre')) {
        return (
          <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-foreground">
            {domToReact(domNode.children as DOMNode[], options)}
          </code>
        )
      }

      if (domNode.name === 'strong') {
        return (
          <strong className="font-semibold text-foreground">
            {domToReact(domNode.children as DOMNode[], options)}
          </strong>
        )
      }

      if (domNode.name === 'em') {
        return (
          <em className="italic text-foreground">
            {domToReact(domNode.children as DOMNode[], options)}
          </em>
        )
      }

      if (domNode.name === 'img') {
        return (
          <img
            {...domNode.attribs}
            loading="lazy"
            className="rounded-lg shadow-md max-w-full h-auto my-4"
          />
        )
      }
    },
  }

  return (
    <div className={className}>
      {parse(result.markup, options)}
    </div>
  )
}
